import classnames from 'classnames';
import { useController, type Control, type FieldValues, type Path } from 'react-hook-form';
import { Select as CommonSelect } from 'components/common';
import Error from './Error';
import Label from './Label';
import type { FormError } from './types';

interface SelectOption {
  label?: string;
  value?: number | string;
}

interface SelectProps<T extends FieldValues> {
  control: Control<T>;
  disabled?: boolean;
  error?: FormError;
  isMulti?: boolean;
  label: string;
  name: Path<T>;
  options: SelectOption[];
  overflowContainer?: boolean;
  placeholder?: string;
  testId?: string | null;
}

const Select = <T extends FieldValues>({
  control,
  disabled = false,
  error,
  isMulti = false,
  label,
  name,
  options,
  overflowContainer = false,
  placeholder = 'Select an option',
  testId = null,
}: SelectProps<T>) => {
  const { field } = useController({ control, name });

  const handleSelected = (opts: SelectOption[], values: any) =>
    isMulti
      ? opts.filter((item) => values?.some((value: any) => value === item.value))
      : opts.find((item) => item.value === values);

  const handleChange = (selectedOption: any) =>
    isMulti
      ? field.onChange(selectedOption?.map((option: SelectOption) => option.value) ?? []) // When multiple selections are allowed, return an array of selected options
      : field.onChange(selectedOption ? selectedOption.value : null); // Single selection - only one value

  return (
    <div className="my-3">
      <Label name={field.name}>{label}</Label>
      <CommonSelect
        className={classnames('form-react-select', { 'is-invalid': error })}
        disabled={disabled}
        isMulti={isMulti}
        onChange={handleChange}
        options={options}
        overflowContainer={overflowContainer}
        placeholder={placeholder}
        selected={handleSelected(options, field.value)}
        testId={testId as string | undefined}
      />
      <Error>{error?.message}</Error>
    </div>
  );
};

export default Select;
